import { useEffect, useState } from "react";
import { supabase } from "../lib/supabase";
import { useAuth } from "../context/AuthContext";
import { completeServiceBooking } from "../utils/marketplaceOps";
import {
  canAssignNewJobs,
  getEffectiveWorkerStatus,
  isWorkerVisibleWindow,
} from "../utils/workerAvailability";
import {
  fetchEarningsTimelineForWorker,
  fetchWorkerRecoveryState,
  fetchWalletSnapshot,
  formatMoney,
  getFinanceStateSummary,
  getRecoveryModeMeta,
  updateServiceBookingLifecycle,
} from "../utils/marketplaceLifecycle";
import { syncWorkerAvailabilityRecord } from "../utils/syncWorkerAvailability";

const SKILL_OPTIONS = [
  "Plumbing",
  "Electrical",
  "Carpentry",
  "Painting",
  "Masonry",
  "Tile Work",
  "Welding",
  "False Ceiling",
  "Waterproofing",
  "Fabrication",
  "CCTV",
  "Solar",
  "AC Repair",
  "Helper",
];

const LANGUAGE_OPTIONS = [
  "Telugu",
  "Hindi",
  "English",
  "Urdu",
  "Tamil",
  "Kannada",
  "Marathi",
];

const JOB_FILTERS = [
  { id: "all", label: "All" },
  { id: "pending", label: "Pending" },
  { id: "accepted", label: "Accepted" },
  { id: "completed", label: "Completed" },
];

function WorkerDashboard() {
  const { user, profile, loading: authLoading } = useAuth();

  const [worker, setWorker] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [assignedBookings, setAssignedBookings] = useState([]);
  const [legacyAssignedBookings, setLegacyAssignedBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [jobFilter, setJobFilter] = useState("all");
  const [walletSnapshot, setWalletSnapshot] = useState(null);
  const [earningsTimeline, setEarningsTimeline] = useState([]);
  const [workerRecoveryState, setWorkerRecoveryState] = useState(null);

  const [formData, setFormData] = useState({
    location: "",
    wallet_number: "",
    online_status: "offline",
    skills: [],
    languages: [],
    terms_accepted: false,
  });

  const parseCsv = (value) => {
    if (!value) return [];
    return value
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean);
  };

  useEffect(() => {
    if (authLoading) return;
    if (!user || profile?.role !== "worker") {
      setLoading(false);
      return;
    }

    fetchWorkerData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, user?.id, profile?.role]);

  const fetchWorkerData = async () => {
    try {
      setLoading(true);
      let profileCity = profile?.city || null;

      const { data: workerData, error: workerError } = await supabase
        .from("workers")
        .select("*")
        .eq("profile_id", user.id)
        .maybeSingle();

      if (workerError) throw workerError;

      let finalWorker = workerData;

      if (!finalWorker) {
        const { data: insertedWorker, error: insertError } = await supabase
          .from("workers")
          .insert([
            {
              profile_id: user.id,
              skill: "",
              skills: "",
              languages: "",
              location: "",
              wallet_number: "",
              online_status: "offline",
              terms_accepted: false,
              wallet_balance: 0,
            },
          ])
          .select()
          .single();

        if (insertError) throw insertError;
        finalWorker = insertedWorker;
      }

      const { data: jobsData, error: jobsError } = await supabase
        .from("worker_jobs")
        .select("*")
        .eq("worker_id", finalWorker.id)
        .order("created_at", { ascending: false });

      if (jobsError) throw jobsError;

      if (!profileCity && finalWorker?.profile_id) {
        const { data: workerProfile } = await supabase
          .from("profiles")
          .select("city")
          .eq("id", finalWorker.profile_id)
          .maybeSingle();

        profileCity = workerProfile?.city || null;
      }

      const [assignedBookingsResult, legacyBookingsResult, walletSnapshotResult, earningsTimelineResult, recoveryStateResult] = await Promise.all([
        supabase
          .from("service_bookings")
          .select("*")
          .or(
            `assigned_worker_id.eq.${finalWorker.profile_id || user.id},assigned_worker_id.eq.${finalWorker.id}`
          )
          .order("created_at", { ascending: false }),
        supabase
          .from("bookings")
          .select("*")
          .eq("worker_id", finalWorker.id)
          .order("created_at", { ascending: false }),
        fetchWalletSnapshot(finalWorker.profile_id || user.id),
        fetchEarningsTimelineForWorker(finalWorker.profile_id || user.id),
        fetchWorkerRecoveryState(finalWorker.profile_id || user.id),
      ]);

      if (assignedBookingsResult.error) throw assignedBookingsResult.error;
      if (legacyBookingsResult.error) throw legacyBookingsResult.error;

      const assignedBookingsRows = assignedBookingsResult.data || [];
      const assignedOrderIds = assignedBookingsRows
        .map((booking) => booking.order_id)
        .filter(Boolean);

      let orderAddressMap = {};
      let orderCodeMap = {};

      if (assignedOrderIds.length > 0) {
        const { data: orderRows, error: orderRowsError } = await supabase
          .from("orders")
          .select(`
            id,
            order_code,
            addresses (
              house_flat_no,
              street_area,
              city,
              state,
              country,
              pincode
            )
          `)
          .in("id", assignedOrderIds);

        if (orderRowsError) throw orderRowsError;

        orderAddressMap = (orderRows || []).reduce((acc, orderRow) => {
          const address = orderRow.addresses;
          acc[orderRow.id] = [
            address?.house_flat_no,
            address?.street_area,
            address?.city,
            address?.state,
            address?.country,
            address?.pincode,
          ]
            .filter(Boolean)
            .join(", ");
          return acc;
        }, {});

        orderCodeMap = (orderRows || []).reduce((acc, orderRow) => {
          acc[orderRow.id] = orderRow.order_code || orderRow.id;
          return acc;
        }, {});
      }

      const allJobs = jobsData || [];
      const effectiveStatus = getEffectiveWorkerStatus(
        finalWorker.online_status,
        allJobs
      );

      if (effectiveStatus !== finalWorker.online_status) {
        await supabase
          .from("workers")
          .update({ online_status: effectiveStatus })
          .eq("id", finalWorker.id);

        finalWorker = {
          ...finalWorker,
          online_status: effectiveStatus,
        };
      }

      await syncWorkerAvailabilityRecord(finalWorker, profileCity);

      setWorker(finalWorker);
      setJobs(allJobs);
      setAssignedBookings(
        assignedBookingsRows.map((booking) => ({
          ...booking,
          orderCode: orderCodeMap[booking.order_id] || booking.order_id || "N/A",
          resolvedAddressText:
            booking.address_text || orderAddressMap[booking.order_id] || "",
        }))
      );
      setLegacyAssignedBookings(legacyBookingsResult.data || []);
      setWalletSnapshot(walletSnapshotResult);
      setEarningsTimeline(earningsTimelineResult);
      setWorkerRecoveryState(recoveryStateResult);

      setFormData({
        location: finalWorker.location || "",
        wallet_number: finalWorker.wallet_number || "",
        online_status: finalWorker.online_status || "offline",
        skills: parseCsv(finalWorker.skills || finalWorker.skill || ""),
        languages: parseCsv(finalWorker.languages || ""),
        terms_accepted: finalWorker.terms_accepted || false,
      });
    } catch (error) {
      console.error("Worker dashboard error:", error);
      alert(error.message || "Failed to load worker dashboard.");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const toggleArrayValue = (field, value) => {
    setFormData((prev) => {
      const exists = prev[field].includes(value);

      return {
        ...prev,
        [field]: exists
          ? prev[field].filter((item) => item !== value)
          : [...prev[field], value],
      };
    });
  };

  const setAvailability = (value) => {
    if (!isWorkerVisibleWindow()) {
      alert("Worker can stay visible only between 5:30 AM and 6:30 PM.");
      return;
    }

    if (!canAssignNewJobs() && value === "online") {
      alert("No new jobs can be made active after 6:00 PM.");
      return;
    }

    setFormData((prev) => ({
      ...prev,
      online_status: value,
    }));
  };

  const handleSave = async () => {
    try {
      if (!worker) return;

      if (!formData.terms_accepted) {
        alert("Please accept terms and conditions.");
        return;
      }

      const skillsCsv = formData.skills.join(", ");
      const languagesCsv = formData.languages.join(", ");

      const { error } = await supabase
        .from("workers")
        .update({
          location: formData.location,
          wallet_number: formData.wallet_number,
          online_status: formData.online_status,
          skills: skillsCsv,
          skill: skillsCsv,
          languages: languagesCsv,
          terms_accepted: formData.terms_accepted,
        })
        .eq("id", worker.id);

      if (error) throw error;

      await syncWorkerAvailabilityRecord(
        {
          ...worker,
          location: formData.location,
          wallet_number: formData.wallet_number,
          online_status: formData.online_status,
          skills: skillsCsv,
          skill: skillsCsv,
          languages: languagesCsv,
          terms_accepted: formData.terms_accepted,
        },
        profile?.city || null
      );

      alert("Worker profile updated.");
      fetchWorkerData();
    } catch (error) {
      console.error("Save worker error:", error);
      alert(error.message || "Failed to update worker profile.");
    }
  };

  const updateJobStatus = async (jobId, status) => {
    try {
      const { error } = await supabase
        .from("worker_jobs")
        .update({ status })
        .eq("id", jobId);

      if (error) throw error;

      fetchWorkerData();
    } catch (error) {
      console.error("Update job error:", error);
      alert(error.message || "Failed to update job status.");
    }
  };

  const updateAssignedBookingStatus = async (bookingId, status) => {
    try {
      const booking = assignedBookings.find((item) => item.id === bookingId);
      let result;

      if (status === "completed") {
        const completionResult = await completeServiceBooking({
          bookingId,
          action: "complete_only",
          notes: "Worker submitted completion from dashboard.",
        });

        result = {
          assignmentStatus: "completed",
          financeState: completionResult?.financeState || "verification_hold",
        };
      } else {
        result = await updateServiceBookingLifecycle({
          bookingId,
          workerProfileId: booking?.assigned_worker_id || worker?.profile_id || user?.id,
          actorProfileId: user?.id,
          status,
          visibleAmount:
            booking?.final_project_value ||
            booking?.rate ||
            booking?.payment_collected ||
            0,
        });
      }

      setAssignedBookings((prev) =>
        prev.map((booking) =>
          booking.id === bookingId
            ? {
                ...booking,
                status,
                ...(result.assignmentStatus
                  ? { assignment_status: result.assignmentStatus }
                  : {}),
                ...(result.financeState
                  ? { latestFinanceState: result.financeState }
                  : {}),
              }
            : booking
        )
      );

      if (status === "completed" || status === "in_progress") {
        const [walletSnapshotResult, earningsTimelineResult, recoveryStateResult] = await Promise.all([
          fetchWalletSnapshot(worker?.profile_id || user?.id),
          fetchEarningsTimelineForWorker(worker?.profile_id || user?.id),
          fetchWorkerRecoveryState(worker?.profile_id || user?.id),
        ]);

        setWalletSnapshot(walletSnapshotResult);
        setEarningsTimeline(earningsTimelineResult);
        setWorkerRecoveryState(recoveryStateResult);
      }
    } catch (error) {
      console.error("Update assigned booking error:", error);
      alert(error.message || "Failed to update assigned booking status.");
    }
  };

  const getTransportDisplayLabel = (booking) => {
    if (booking?.transport_responsibility === "customer_arranged") {
      return "Customer will bear transport";
    }

    if (booking?.transport_responsibility === "mistribazaar_arranged") {
      return "MistriBazaar will arrange transport";
    }

    const notes = booking?.notes || "";

    if (notes.includes("Transport responsibility: customer_arranged")) {
      return "Customer will bear transport";
    }

    if (notes.includes("Transport responsibility: mistribazaar_arranged")) {
      return "MistriBazaar will arrange transport";
    }

    return "Transport not specified";
  };

  const jobsByStatus = jobs.reduce(
    (summary, job) => {
      const status = job.status || "pending";

      if (status === "pending") summary.pending += 1;
      if (status === "accepted" || status === "in_progress") summary.accepted += 1;
      if (status === "completed") summary.completed += 1;

      summary.total += 1;
      return summary;
    },
    { total: 0, pending: 0, accepted: 0, completed: 0 }
  );

  const bookingSummary = assignedBookings.reduce(
    (summary, booking) => {
      const status = booking.status || "pending";

      if (["accepted", "in_progress", "worker_assigned", "worker_reassigned"].includes(status)) {
        summary.active += 1;
      } else if (status === "completed") {
        summary.completed += 1;
      } else {
        summary.pending += 1;
      }

      summary.total += 1;
      return summary;
    },
    { total: 0, pending: 0, active: 0, completed: 0 }
  );

  const legacyBookingSummary = legacyAssignedBookings.reduce(
    (summary, booking) => {
      const status = booking.status || "pending";

      if (["accepted", "in_progress"].includes(status)) {
        summary.active += 1;
      } else if (status === "completed") {
        summary.completed += 1;
      } else {
        summary.pending += 1;
      }

      summary.total += 1;
      return summary;
    },
    { total: 0, pending: 0, active: 0, completed: 0 }
  );

  const visibleJobs = jobs.filter((job) => {
    if (jobFilter === "all") return true;
    if (jobFilter === "accepted") {
      return ["accepted", "in_progress"].includes(job.status);
    }
    return (job.status || "pending") === jobFilter;
  });

  const assignmentReadinessChecks = [
    {
      label: "Worker is online",
      ready: formData.online_status === "online",
    },
    {
      label: "Terms accepted",
      ready: Boolean(formData.terms_accepted),
    },
    {
      label: "Live city allowed",
      ready: Boolean(worker?.live_city_allowed),
    },
    {
      label: "Profile city available",
      ready: Boolean(worker?.city || profile?.city),
    },
    {
      label: "At least one skill selected",
      ready: formData.skills.length > 0,
    },
  ];

  const missingAssignmentReadiness = assignmentReadinessChecks.filter(
    (item) => !item.ready
  );
  const recoveryMeta = getRecoveryModeMeta(workerRecoveryState);

  if (authLoading || loading) {
    return (
      <div style={page} className="worker-dashboard">
        <p>Loading worker dashboard...</p>
      </div>
    );
  }

  if (!user || profile?.role !== "worker") {
    return (
      <div style={page} className="worker-dashboard">
        <p>Access denied.</p>
      </div>
    );
  }

  return (
    <div style={page} className="worker-dashboard">
      <div style={heroCard} className="worker-dashboard__hero">
        <h1 style={{ marginTop: 0 }}>Worker Dashboard</h1>
        <p style={mutedBig}>
          Manage your availability, skills, jobs, wallet, and referral progress.
        </p>
      </div>

      <div style={summaryGrid} className="worker-dashboard__summary-grid">
        <div style={summaryCard}>
          <div style={summaryLabel}>Withdrawable Balance</div>
          <div style={summaryValue}>
            {formatMoney(
              walletSnapshot?.withdrawable_balance ?? worker?.wallet_balance ?? 0
            )}
          </div>
          <div style={summarySubtext}>
            {walletSnapshot
              ? `Secured ${formatMoney(walletSnapshot.secured_earnings)} • Weekly release cycle: Saturday`
              : "Weekly release cycle: Saturday"}
          </div>
        </div>

        <div style={summaryCard}>
          <div style={summaryLabel}>Completed Bookings</div>
          <div style={summaryValue}>
            {worker?.completed_bookings_count || 0}
          </div>
          <div style={summarySubtext}>Referral unlock target: 30</div>
        </div>

        <div style={summaryCard}>
          <div style={summaryLabel}>Referral Bonus Earned</div>
          <div style={summaryValue}>
            ₹{Number(worker?.referral_bonus_earned || 0).toLocaleString()}
          </div>
          <div style={summarySubtext}>Dynamic up to ₹3000</div>
        </div>

        <div style={summaryCard}>
          <div style={summaryLabel}>Status</div>
          <div
            style={{
              ...statusPill,
              background:
                formData.online_status === "online" ? "#10301a" : "#2a1111",
              color:
                formData.online_status === "online" ? "#7CFC00" : "#ff9a9a",
            }}
          >
            {formData.online_status === "online" ? "Online" : "Offline"}
          </div>
          <div style={summarySubtext}>Visible: 5:30 AM to 6:30 PM</div>
        </div>
      </div>

      <div style={card} className="worker-dashboard__card">
        <h2>My Profile</h2>

        <div style={profileGrid} className="worker-dashboard__profile-grid">
          <div style={profileBox}>
            <div style={profileLabel}>Full Name</div>
            <div style={profileValue}>{profile?.full_name || "N/A"}</div>
          </div>

          <div style={profileBox}>
            <div style={profileLabel}>Phone</div>
            <div style={profileValue}>{profile?.phone || "N/A"}</div>
          </div>

          <div style={profileBox}>
            <div style={profileLabel}>Email</div>
            <div style={profileValue}>{profile?.email || "N/A"}</div>
          </div>

          <div style={profileBox}>
            <div style={profileLabel}>Unique ID</div>
            <div style={profileValue}>{profile?.display_user_id || "N/A"}</div>
          </div>

          <div style={profileBox}>
            <div style={profileLabel}>Referral Code</div>
            <div style={profileValue}>{worker?.referral_code || "Not set yet"}</div>
          </div>

          <div style={profileBox}>
            <div style={profileLabel}>Referred By</div>
            <div style={profileValue}>{worker?.referred_by_code || "N/A"}</div>
          </div>
        </div>
      </div>

      <div style={card} className="worker-dashboard__card">
        <h2>Work Settings</h2>

        <div
          style={{
            ...readinessBox,
            borderColor:
              missingAssignmentReadiness.length === 0 ? "#28593a" : "#5a4a16",
            background:
              missingAssignmentReadiness.length === 0 ? "#102516" : "#191408",
          }}
        >
          <strong style={{ display: "block", marginBottom: "8px" }}>
            Assignment Readiness
          </strong>
          <p style={{ ...muted, marginTop: 0 }}>
            {missingAssignmentReadiness.length === 0
              ? "This worker profile looks ready for live auto-assignment when matching demand appears."
              : `Auto-assignment may not reach this worker yet. Missing: ${missingAssignmentReadiness
                  .map((item) => item.label)
                  .join(", ")}.`}
          </p>
        </div>

        <label style={label}>Service Location / Area</label>
        <input
          name="location"
          placeholder="Example: Madhapur, Kukatpally, Gachibowli"
          value={formData.location}
          onChange={handleChange}
          style={input}
        />

        <label style={label}>Wallet / UPI Number</label>
        <input
          name="wallet_number"
          placeholder="UPI ID or wallet number"
          value={formData.wallet_number}
          onChange={handleChange}
          style={input}
        />

        <label style={label}>Availability</label>
        <div style={toggleWrap} className="worker-dashboard__toggle-wrap">
          <button
            type="button"
            onClick={() => setAvailability("online")}
            style={{
              ...toggleBtn,
              background:
                formData.online_status === "online" ? "#fff" : "#111",
              color: formData.online_status === "online" ? "#000" : "#fff",
            }}
          >
            Online
          </button>

          <button
            type="button"
            onClick={() => setAvailability("offline")}
            style={{
              ...toggleBtn,
              background:
                formData.online_status === "offline" ? "#fff" : "#111",
              color: formData.online_status === "offline" ? "#000" : "#fff",
            }}
          >
            Offline
          </button>
        </div>

        <label style={label}>Skills</label>
        <div style={chipWrap} className="worker-dashboard__chip-wrap">
          {SKILL_OPTIONS.map((skill) => {
            const selected = formData.skills.includes(skill);

            return (
              <button
                key={skill}
                type="button"
                onClick={() => toggleArrayValue("skills", skill)}
                style={{
                  ...chipBtn,
                  background: selected ? "#fff" : "#111",
                  color: selected ? "#000" : "#fff",
                }}
              >
                {selected ? "✓ " : ""}{skill}
              </button>
            );
          })}
        </div>

        <label style={label}>Languages</label>
        <div style={chipWrap} className="worker-dashboard__chip-wrap">
          {LANGUAGE_OPTIONS.map((language) => {
            const selected = formData.languages.includes(language);

            return (
              <button
                key={language}
                type="button"
                onClick={() => toggleArrayValue("languages", language)}
                style={{
                  ...chipBtn,
                  background: selected ? "#fff" : "#111",
                  color: selected ? "#000" : "#fff",
                }}
              >
                {selected ? "✓ " : ""}{language}
              </button>
            );
          })}
        </div>

        <label style={checkboxRow}>
          <input
            type="checkbox"
            name="terms_accepted"
            checked={formData.terms_accepted}
            onChange={handleChange}
          />
          <span>I accept terms and conditions</span>
        </label>

        <button onClick={handleSave} style={button}>
          Save Worker Profile
        </button>
      </div>

      <div style={card} className="worker-dashboard__card">
        <h2>My Gigs / Jobs</h2>

        <div style={jobsSummaryGrid} className="worker-dashboard__jobs-summary-grid">
          <div style={jobsSummaryCard}>
            <div style={jobsSummaryLabel}>Pending</div>
            <div style={jobsSummaryValue}>{jobsByStatus.pending}</div>
          </div>
          <div style={jobsSummaryCard}>
            <div style={jobsSummaryLabel}>Accepted</div>
            <div style={jobsSummaryValue}>{jobsByStatus.accepted}</div>
          </div>
          <div style={jobsSummaryCard}>
            <div style={jobsSummaryLabel}>Completed</div>
            <div style={jobsSummaryValue}>{jobsByStatus.completed}</div>
          </div>
          <div style={jobsSummaryCard}>
            <div style={jobsSummaryLabel}>Total</div>
            <div style={jobsSummaryValue}>{jobsByStatus.total}</div>
          </div>
        </div>

        <div style={chipWrap} className="worker-dashboard__chip-wrap">
          {JOB_FILTERS.map((filter) => {
            const selected = jobFilter === filter.id;

            return (
              <button
                key={filter.id}
                type="button"
                onClick={() => setJobFilter(filter.id)}
                style={{
                  ...chipBtn,
                  background: selected ? "#fff" : "#111",
                  color: selected ? "#000" : "#fff",
                }}
              >
                {filter.label}
              </button>
            );
          })}
        </div>

        {visibleJobs.length === 0 ? (
          <div style={emptyBox}>
            <p>
              {jobs.length === 0
                ? "No jobs assigned yet."
                : "No jobs in this status right now."}
            </p>
          </div>
        ) : (
          <div style={{ display: "grid", gap: "14px" }}>
            {visibleJobs.map((job) => (
              <div key={job.id} style={innerCard}>
                <div style={jobHeader} className="worker-dashboard__job-header">
                  <div>
                    <h3 style={{ marginTop: 0, marginBottom: "8px" }}>
                      {job.title}
                    </h3>
                    <p style={muted}>
                      <strong>Date:</strong> {job.job_date || "N/A"}
                    </p>
                    <p style={muted}>
                      <strong>Status:</strong> {formatStatus(job.status)}
                    </p>
                  </div>

                  <div style={jobActions} className="worker-dashboard__job-actions">
                    <button
                      onClick={() => updateJobStatus(job.id, "accepted")}
                      style={button}
                    >
                      Accept
                    </button>

                    <button
                      onClick={() => updateJobStatus(job.id, "declined")}
                      style={outlineBtn}
                    >
                      Decline
                    </button>

                    <button
                      onClick={() => updateJobStatus(job.id, "completed")}
                      style={outlineBtn}
                    >
                      Complete
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {workerRecoveryState ? (
        <div
          style={{
            ...card,
            borderColor:
              recoveryMeta.tone === "good"
                ? "#245235"
                : recoveryMeta.tone === "watch"
                  ? "#5b4f1d"
                  : "#6f2a2a",
            background:
              recoveryMeta.tone === "good"
                ? "#102516"
                : recoveryMeta.tone === "watch"
                  ? "#191408"
                  : "#1e0e0e",
          }}
          className="worker-dashboard__card"
        >
          <h2>Recovery Status</h2>
          <p style={muted}>
            <strong>Status:</strong> {recoveryMeta.label}
          </p>
          <p style={muted}>{recoveryMeta.description}</p>
          <p style={muted}>
            <strong>Settlement Due:</strong> {formatMoney(workerRecoveryState.current_settlement_due)}
          </p>
          <p style={muted}>
            <strong>Cash Jobs Allowed:</strong> {workerRecoveryState.cash_jobs_allowed ? "Yes" : "No"}
          </p>
          <p style={muted}>
            <strong>Digital Priority:</strong> {workerRecoveryState.digital_priority_only ? "Yes" : "No"}
          </p>
          {workerRecoveryState.settlement_restriction_reason ? (
            <p style={muted}>
              <strong>Why:</strong> {workerRecoveryState.settlement_restriction_reason}
            </p>
          ) : null}
          {(workerRecoveryState.exposures || []).length > 0 ? (
            <div style={{ display: "grid", gap: "8px", marginTop: "12px" }}>
              {(workerRecoveryState.exposures || []).slice(0, 3).map((exposure) => (
                <div key={exposure.id} style={innerCard}>
                  <p style={muted}>
                    <strong>{String(exposure.exposure_type || "exposure").replaceAll("_", " ")}</strong>
                  </p>
                  <p style={muted}>
                    Outstanding: {formatMoney(exposure.outstanding_amount)} • {String(exposure.aging_bucket || "0_7_days").replaceAll("_", " ")}
                  </p>
                </div>
              ))}
            </div>
          ) : null}
        </div>
      ) : null}

      <div style={card} className="worker-dashboard__card">
        <h2>Marketplace Wallet</h2>
        <div style={jobsSummaryGrid} className="worker-dashboard__jobs-summary-grid">
          <div style={jobsSummaryCard}>
            <div style={jobsSummaryLabel}>Visible Earnings</div>
            <div style={jobsSummaryValue}>
              {formatMoney(walletSnapshot?.visible_earnings ?? 0)}
            </div>
          </div>
          <div style={jobsSummaryCard}>
            <div style={jobsSummaryLabel}>Secured Earnings</div>
            <div style={jobsSummaryValue}>
              {formatMoney(walletSnapshot?.secured_earnings ?? 0)}
            </div>
          </div>
          <div style={jobsSummaryCard}>
            <div style={jobsSummaryLabel}>Transfer Queue</div>
            <div style={jobsSummaryValue}>
              {formatMoney(walletSnapshot?.scheduled_for_transfer ?? 0)}
            </div>
          </div>
          <div style={jobsSummaryCard}>
            <div style={jobsSummaryLabel}>Recovery Due</div>
            <div style={jobsSummaryValue}>
              {formatMoney(walletSnapshot?.recovery_due ?? 0)}
            </div>
          </div>
        </div>
        <p style={muted}>
          Work completion, secured earnings, and transfer eligibility are shown separately so payout status stays clearer.
        </p>
      </div>

      <div style={card} className="worker-dashboard__card">
        <h2>Assigned Service Bookings</h2>

        <div style={jobsSummaryGrid} className="worker-dashboard__jobs-summary-grid">
          <div style={jobsSummaryCard}>
            <div style={jobsSummaryLabel}>Pending</div>
            <div style={jobsSummaryValue}>{bookingSummary.pending}</div>
          </div>
          <div style={jobsSummaryCard}>
            <div style={jobsSummaryLabel}>Active</div>
            <div style={jobsSummaryValue}>{bookingSummary.active}</div>
          </div>
          <div style={jobsSummaryCard}>
            <div style={jobsSummaryLabel}>Completed</div>
            <div style={jobsSummaryValue}>{bookingSummary.completed}</div>
          </div>
          <div style={jobsSummaryCard}>
            <div style={jobsSummaryLabel}>Total</div>
            <div style={jobsSummaryValue}>{bookingSummary.total}</div>
          </div>
        </div>

        {assignedBookings.length === 0 ? (
          <div style={emptyBox}>
            <p>No service bookings assigned yet.</p>
          </div>
        ) : (
          <div style={{ display: "grid", gap: "14px" }}>
            {assignedBookings.map((booking) => (
              <div key={booking.id} style={innerCard}>
                <div style={jobHeader} className="worker-dashboard__job-header">
                  <div>
                    <h3 style={{ marginTop: 0, marginBottom: "8px" }}>
                      {booking.service_title || booking.service_type || "Service Booking"}
                    </h3>
                    <p style={muted}>
                      <strong>Date:</strong> {booking.booking_date || "N/A"}
                    </p>
                    <p style={muted}>
                      <strong>Slot:</strong> {booking.booking_slot || "To be assigned"}
                    </p>
                    <p style={muted}>
                      <strong>Status:</strong> {formatStatus(booking.status)}
                    </p>
                    <p style={muted}>
                      <strong>Order ID:</strong> {booking.orderCode}
                    </p>
                    <p style={muted}>
                      <strong>Address:</strong>{" "}
                      {booking.resolvedAddressText || booking.address_text || "N/A"}
                    </p>
                    <p style={muted}>
                      <strong>Transport:</strong> {getTransportDisplayLabel(booking)}
                    </p>
                    {(() => {
                      const financeEntry = earningsTimeline.find(
                        (entry) => entry.service_booking_id === booking.id
                      );
                      const financeSummary = getFinanceStateSummary(financeEntry);

                      if (!financeSummary) return null;

                      return (
                        <>
                          <p style={muted}>
                            <strong>Earnings State:</strong> {financeSummary.workerLabel}
                          </p>
                          <p style={muted}>
                            <strong>Visible Amount:</strong> {formatMoney(financeSummary.visibleAmount)}
                          </p>
                        </>
                      );
                    })()}
                  </div>
                  <div style={jobActions} className="worker-dashboard__job-actions">
                    <button
                      onClick={() =>
                        updateAssignedBookingStatus(booking.id, "accepted")
                      }
                      style={button}
                    >
                      Accept
                    </button>

                    <button
                      onClick={() =>
                        updateAssignedBookingStatus(booking.id, "in_progress")
                      }
                      style={outlineBtn}
                    >
                      Start
                    </button>

                    <button
                      onClick={() =>
                        updateAssignedBookingStatus(booking.id, "completed")
                      }
                      style={outlineBtn}
                    >
                      Complete
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div style={card} className="worker-dashboard__card">
        <h2>Direct Worker Bookings</h2>

        <div style={jobsSummaryGrid} className="worker-dashboard__jobs-summary-grid">
          <div style={jobsSummaryCard}>
            <div style={jobsSummaryLabel}>Pending</div>
            <div style={jobsSummaryValue}>{legacyBookingSummary.pending}</div>
          </div>
          <div style={jobsSummaryCard}>
            <div style={jobsSummaryLabel}>Active</div>
            <div style={jobsSummaryValue}>{legacyBookingSummary.active}</div>
          </div>
          <div style={jobsSummaryCard}>
            <div style={jobsSummaryLabel}>Completed</div>
            <div style={jobsSummaryValue}>{legacyBookingSummary.completed}</div>
          </div>
          <div style={jobsSummaryCard}>
            <div style={jobsSummaryLabel}>Total</div>
            <div style={jobsSummaryValue}>{legacyBookingSummary.total}</div>
          </div>
        </div>

        {legacyAssignedBookings.length === 0 ? (
          <div style={emptyBox}>
            <p>No direct worker bookings assigned yet.</p>
          </div>
        ) : (
          <div style={{ display: "grid", gap: "14px" }}>
            {legacyAssignedBookings.map((booking) => (
              <div key={booking.id} style={innerCard}>
                <div style={jobHeader} className="worker-dashboard__job-header">
                  <div>
                    <h3 style={{ marginTop: 0, marginBottom: "8px" }}>
                      {booking.service_type || "Direct Worker Booking"}
                    </h3>
                    <p style={muted}>
                      <strong>Date:</strong> {booking.booking_date || "N/A"}
                    </p>
                    <p style={muted}>
                      <strong>Slot:</strong> {booking.booking_slot || "To be assigned"}
                    </p>
                    <p style={muted}>
                      <strong>Status:</strong> {formatStatus(booking.status)}
                    </p>
                    <p style={muted}>
                      <strong>Booking Ref:</strong> {booking.display_user_id || booking.id}
                    </p>
                    <p style={muted}>
                      <strong>Address:</strong>{" "}
                      {booking.address_text ||
                        [
                          booking.flat_house_no,
                          booking.street_area,
                          booking.city,
                          booking.state,
                          booking.country,
                          booking.pincode,
                        ]
                          .filter(Boolean)
                          .join(", ") ||
                        "N/A"}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div style={card} className="worker-dashboard__card">
        <h2>Wallet Rules</h2>
        <ul style={list}>
          <li>Work completion does not mean instant payout. Final checks happen first.</li>
          <li>Secured earnings are shown separately from transfer-ready balance.</li>
          <li>Cash orders can still create settlement due if platform fees remain unpaid.</li>
          <li>Recovery due appears separately so deductions do not feel hidden.</li>
        </ul>
      </div>

      <div style={card} className="worker-dashboard__card">
        <h2>Referral Program</h2>
        <p style={muted}>
          If a worker refers another worker and the referred person completes 30
          bookings, both can receive a dynamic referral bonus up to ₹3000 each,
          while keeping platform economics under control.
        </p>
      </div>
    </div>
  );
}

function formatStatus(status) {
  if (!status) return "Pending";
  return status
    .replaceAll("_", " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

const page = {
  background: "#000",
  minHeight: "100vh",
  padding: "40px",
  color: "#fff",
  fontFamily: "Arial, sans-serif",
  maxWidth: "1100px",
  margin: "0 auto",
};

const heroCard = {
  background: "linear-gradient(135deg, #111, #191919)",
  padding: "24px",
  borderRadius: "18px",
  border: "1px solid #2a2a2a",
  marginBottom: "20px",
};

const card = {
  background: "#111",
  padding: "22px",
  borderRadius: "16px",
  border: "1px solid #2a2a2a",
  marginBottom: "20px",
};

const innerCard = {
  background: "#0b0b0b",
  padding: "18px",
  borderRadius: "14px",
  border: "1px solid #222",
};

const emptyBox = {
  background: "#0b0b0b",
  padding: "18px",
  borderRadius: "12px",
  border: "1px solid #222",
};

const summaryGrid = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
  gap: "14px",
  marginBottom: "20px",
};

const summaryCard = {
  background: "#111",
  border: "1px solid #2a2a2a",
  borderRadius: "16px",
  padding: "18px",
};

const summaryLabel = {
  color: "#9a9a9a",
  fontSize: "13px",
  marginBottom: "8px",
};

const summaryValue = {
  color: "#fff",
  fontWeight: "bold",
  fontSize: "24px",
};

const summarySubtext = {
  color: "#bdbdbd",
  fontSize: "12px",
  marginTop: "8px",
};

const statusPill = {
  display: "inline-block",
  padding: "10px 14px",
  borderRadius: "999px",
  fontWeight: "bold",
};

const profileGrid = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
  gap: "14px",
  marginTop: "12px",
};

const profileBox = {
  background: "#0b0b0b",
  border: "1px solid #222",
  borderRadius: "12px",
  padding: "16px",
};

const profileLabel = {
  color: "#aaa",
  fontSize: "13px",
  marginBottom: "6px",
};

const profileValue = {
  color: "#fff",
  fontWeight: "bold",
  wordBreak: "break-word",
};

const label = {
  display: "block",
  marginBottom: "8px",
  marginTop: "12px",
  color: "#fff",
};

const input = {
  width: "100%",
  padding: "12px 14px",
  marginBottom: "12px",
  borderRadius: "10px",
  border: "1px solid #2a2a2a",
  background: "#0b0b0b",
  color: "#fff",
  boxSizing: "border-box",
};

const readinessBox = {
  border: "1px solid #2a2a2a",
  borderRadius: "12px",
  padding: "14px 16px",
  marginBottom: "16px",
};

const toggleWrap = {
  display: "flex",
  gap: "10px",
  flexWrap: "wrap",
  marginBottom: "14px",
};

const toggleBtn = {
  padding: "12px 18px",
  border: "1px solid #2a2a2a",
  borderRadius: "12px",
  cursor: "pointer",
};

const chipWrap = {
  display: "flex",
  gap: "10px",
  flexWrap: "wrap",
  marginBottom: "14px",
};

const chipBtn = {
  padding: "10px 14px",
  border: "1px solid #2a2a2a",
  borderRadius: "999px",
  cursor: "pointer",
};

const jobsSummaryGrid = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
  gap: "12px",
  marginTop: "12px",
  marginBottom: "14px",
};

const jobsSummaryCard = {
  background: "#0b0b0b",
  border: "1px solid #222",
  borderRadius: "12px",
  padding: "14px",
};

const jobsSummaryLabel = {
  color: "#aaa",
  fontSize: "13px",
  marginBottom: "6px",
};

const jobsSummaryValue = {
  color: "#fff",
  fontWeight: "bold",
  fontSize: "24px",
};

const checkboxRow = {
  display: "flex",
  gap: "10px",
  alignItems: "center",
  marginBottom: "16px",
  color: "#fff",
};

const button = {
  padding: "12px 18px",
  border: "none",
  background: "#fff",
  color: "#000",
  borderRadius: "10px",
  cursor: "pointer",
  fontWeight: "bold",
};

const outlineBtn = {
  padding: "12px 18px",
  border: "1px solid #fff",
  background: "#111",
  color: "#fff",
  borderRadius: "10px",
  cursor: "pointer",
};

const muted = {
  color: "#d1d1d1",
  margin: "6px 0",
};

const mutedBig = {
  color: "#d1d1d1",
  lineHeight: "1.7",
  marginBottom: 0,
};

const jobHeader = {
  display: "flex",
  justifyContent: "space-between",
  gap: "16px",
  flexWrap: "wrap",
};

const jobActions = {
  display: "flex",
  gap: "10px",
  flexWrap: "wrap",
  alignItems: "flex-start",
};

const list = {
  color: "#d1d1d1",
  lineHeight: "1.8",
  paddingLeft: "20px",
};

export default WorkerDashboard;
